import { CircleAlertIcon, CircleCheckIcon, InfoIcon, Loader2Icon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

/**
 * The toast host (what Ant Design called `notification`). Mount once, at the
 * root. Toasts are fired with `toast(...)` from `sonner`.
 *
 * Offsets read the `--gg-notification-safe-area-*` variables so toasts clear
 * the notch / status bar on native.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { t } = useTranslation();
  return (
    <Sonner
      theme="light"
      className="toaster group"
      position="top-right"
      closeButton
      // richColors: without it sonner ignores the per-type variables below and every toast looks "normal"
      richColors
      toastOptions={{ closeButtonAriaLabel: t('global.buttons.closeNotification') }}
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <CircleAlertIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      offset={{
        top: 'calc(var(--gg-notification-safe-area-top, 0px) + 16px)',
        bottom: 'calc(var(--gg-notification-safe-area-bottom, 0px) + 16px)',
        left: 'calc(var(--gg-notification-safe-area-left, 0px) + 16px)',
        right: 'calc(var(--gg-notification-safe-area-right, 0px) + 16px)',
      }}
      mobileOffset={{
        top: 'calc(var(--gg-notification-safe-area-top, 0px) + 16px)',
        bottom: 'calc(var(--gg-notification-safe-area-bottom, 0px) + 16px)',
        left: 'calc(var(--gg-notification-safe-area-left, 0px) + 16px)',
        right: 'calc(var(--gg-notification-safe-area-right, 0px) + 16px)',
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--success-bg': 'var(--success-muted)',
          '--success-text': 'var(--success-text)',
          '--success-border': 'var(--success-border)',
          '--info-bg': 'var(--info-muted)',
          '--info-text': 'var(--info-text)',
          '--info-border': 'var(--info-border)',
          '--warning-bg': 'var(--warning-muted)',
          '--warning-text': 'var(--warning-text)',
          '--warning-border': 'var(--warning-border)',
          '--error-bg': 'var(--destructive-muted)',
          '--error-text': 'var(--destructive-text)',
          '--error-border': 'var(--destructive-border)',
          '--border-radius': 'var(--radius)',
          fontFamily: 'var(--font-sans)',
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
